// Enhanced Environment Configuration and Validation
const PROD_FIREBASE_PROJECT_ID = "tutor-app-5e5eb";

export class EnvironmentConfig {
  // Environment Detection
  static get isDevelopment() {
    return (
      process.env.NODE_ENV === "development" ||
      process.env.VUE_APP_ENVIRONMENT === "development" ||
      process.env.VUE_APP_ENVIRONMENT === "local"
    );
  }

  static get isStaging() {
    return process.env.VUE_APP_ENVIRONMENT === "staging";
  }

  static get isProduction() {
    return (
      (process.env.NODE_ENV === "production" &&
        process.env.VUE_APP_ENVIRONMENT === "production") ||
      process.env.VUE_APP_ENVIRONMENT === "production"
    );
  }

  static get isLocal() {
    return (
      process.env.VUE_APP_ENVIRONMENT === "local" ||
      (process.env.NODE_ENV === "development" &&
        !process.env.VUE_APP_ENVIRONMENT)
    );
  }

  static get environment() {
    return (
      process.env.VUE_APP_ENVIRONMENT ||
      (process.env.NODE_ENV === "development"
        ? "local"
        : process.env.NODE_ENV) ||
      "local"
    );
  }

  // Build and Runtime Information
  static get buildInfo() {
    return {
      version:
        process.env.VUE_APP_VERSION ||
        process.env.npm_package_version ||
        "0.1.0",
      buildTime: process.env.VUE_APP_BUILD_TIME || new Date().toISOString(),
      gitCommit: process.env.VUE_APP_GIT_COMMIT || "unknown",
      gitBranch: process.env.VUE_APP_GIT_BRANCH || "unknown",
      environment: this.environment,
    };
  }

  static get debugMode() {
    return process.env.VUE_APP_DEBUG_MODE === "true" || this.isDevelopment;
  }

  static get logLevel() {
    return (
      process.env.VUE_APP_LOG_LEVEL || (this.isDevelopment ? "debug" : "warn")
    );
  }

  // Firebase Configuration
  static get firebase() {
    return {
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.VUE_APP_FIREBASE_APP_ID,
    };
  }

  // Stripe Configuration
  static get stripe() {
    return {
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      environment: process.env.VUE_APP_STRIPE_ENVIRONMENT || "test",
    };
  }

  // App Configuration
  static get app() {
    return {
      baseUrl: process.env.VUE_APP_BASE_URL || "http://localhost:8080",
      apiUrl: process.env.VUE_APP_API_URL,
      environment: this.environment,
    };
  }

  // Feature Flags
  static get features() {
    return {
      seeding: process.env.VUE_APP_ENABLE_SEEDING === "true",
      analytics: process.env.VUE_APP_ENABLE_ANALYTICS === "true",
      performanceMonitoring: false,
      debugMode: this.debugMode,
    };
  }

  // Payment Configuration
  static get payment() {
    return {
      platformFeePercentage:
        parseFloat(process.env.VUE_APP_PLATFORM_FEE_PERCENTAGE) || 0.1,
      stripeProcessingFeePercentage:
        parseFloat(process.env.VUE_APP_STRIPE_PROCESSING_FEE_PERCENTAGE) ||
        0.029,
      stripeProcessingFeeFixed:
        parseFloat(process.env.VUE_APP_STRIPE_PROCESSING_FEE_FIXED) || 0.3,
      mockPayments: process.env.VUE_APP_MOCK_PAYMENTS === "true",
      webhookEndpoint: process.env.VUE_APP_STRIPE_WEBHOOK_ENDPOINT,
      currency: process.env.VUE_APP_DEFAULT_CURRENCY || "GBP",
    };
  }

  // API Configuration
  static get api() {
    const baseUrls = {
      local:
        process.env.VUE_APP_API_URL_LOCAL ||
        "http://localhost:5001/tutor-app-5e5eb/us-central1",
      staging:
        process.env.VUE_APP_API_URL_STAGING ||
        "https://us-central1-crescendo-staging-5a0a8.cloudfunctions.net",
      production:
        process.env.VUE_APP_API_URL_PRODUCTION ||
        "https://us-central1-tutor-app-5e5eb.cloudfunctions.net",
    };

    return {
      baseUrl:
        process.env.VUE_APP_API_URL ||
        baseUrls[this.environment] ||
        baseUrls.local,
      timeout: parseInt(process.env.VUE_APP_API_TIMEOUT) || 30000,
      retryAttempts: parseInt(process.env.VUE_APP_API_RETRY_ATTEMPTS) || 3,
      retryDelay: parseInt(process.env.VUE_APP_API_RETRY_DELAY) || 1000,
    };
  }

  // Performance and Monitoring Configuration
  static get monitoring() {
    return {
      analytics: process.env.VUE_APP_ENABLE_ANALYTICS === "true",
      performanceMonitoring: false,
      errorReporting:
        process.env.VUE_APP_ENABLE_ERROR_REPORTING === "true" ||
        this.isProduction,
      hotjar: process.env.VUE_APP_ENABLE_HOTJAR === "true" && this.isProduction,
      googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID,
      sentryDsn: process.env.VUE_APP_SENTRY_DSN,
      hotjarId: process.env.VUE_APP_HOTJAR_ID,
    };
  }

  // Security Configuration
  static get security() {
    return {
      enableCSP: process.env.VUE_APP_ENABLE_CSP === "true" || this.isProduction,
      corsOrigins: process.env.VUE_APP_CORS_ORIGINS
        ? process.env.VUE_APP_CORS_ORIGINS.split(",")
        : ["*"],
      rateLimiting:
        process.env.VUE_APP_ENABLE_RATE_LIMITING === "true" ||
        this.isProduction,
      requireAuth: process.env.VUE_APP_REQUIRE_AUTH !== "false",
    };
  }

  // Cache Configuration
  static get cache() {
    return {
      enabled:
        process.env.VUE_APP_CACHE_ENABLED === "true" || this.isProduction,
      ttl: parseInt(process.env.VUE_APP_CACHE_TTL) || 300000, // 5 minutes default
      maxSize: parseInt(process.env.VUE_APP_CACHE_MAX_SIZE) || 100,
      storage: process.env.VUE_APP_CACHE_STORAGE || "memory", // memory, localStorage, sessionStorage
    };
  }

  // Guardrail: the publishable-key mode must match the backend actually being
  // called. Calling the prod backend (prod project id, emulators off) requires
  // a live key; anything else requires a test key. This catches the test/live
  // mismatch at boot instead of as an opaque Stripe `resource_missing` at Pay.
  static assertStripeKeyModeMatchesBackend() {
    const key = this.stripe.publishableKey;
    if (!key) return; // mock-payments / unconfigured builds are handled elsewhere
    const isLiveKey = key.startsWith("pk_live_");
    const usingEmulators =
      process.env.VUE_APP_USE_EMULATORS === "true" ||
      process.env.VUE_APP_USE_FIREBASE_EMULATORS === "true";
    const callingProdBackend =
      this.firebase.projectId === PROD_FIREBASE_PROJECT_ID && !usingEmulators;

    if (callingProdBackend && !isLiveKey) {
      throw new Error(
        "Stripe key/backend mismatch: a TEST publishable key is targeting the " +
          "PRODUCTION backend. This is the mismatch that makes payments fail with " +
          "`resource_missing`. Use pk_live_ for prod, or point at staging/emulator."
      );
    }
    if (!callingProdBackend && isLiveKey) {
      throw new Error(
        "Stripe key/backend mismatch: a LIVE publishable key is targeting a " +
          "non-production backend. Use pk_test_ for staging/local."
      );
    }
  }

  // Enhanced Validation Functions
  static validateConfiguration() {
    const errors = [];
    const warnings = [];
    const info = [];

    // Validate Environment
    const validEnvironments = ["local", "development", "staging", "production"];
    if (!validEnvironments.includes(this.environment)) {
      errors.push(
        `Invalid environment: ${
          this.environment
        }. Must be one of: ${validEnvironments.join(", ")}`
      );
    }

    // Validate Firebase config
    const fbConfig = this.firebase;
    const requiredFbKeys = [
      "apiKey",
      "authDomain",
      "projectId",
      "storageBucket",
      "messagingSenderId",
      "appId",
    ];

    requiredFbKeys.forEach((key) => {
      if (!fbConfig[key]) {
        if (this.isProduction) {
          errors.push(
            `Missing Firebase configuration: VUE_APP_FIREBASE_${key.toUpperCase()}`
          );
        } else {
          warnings.push(
            `Missing Firebase configuration: VUE_APP_FIREBASE_${key.toUpperCase()}`
          );
        }
      }
    });

    // Enhanced Stripe validation
    const stripeConfig = this.stripe;
    if (!stripeConfig.publishableKey) {
      if (this.payment.mockPayments) {
        warnings.push("Missing Stripe configuration - using mock payments");
      } else {
        errors.push(
          "Missing Stripe configuration: VUE_APP_STRIPE_PUBLISHABLE_KEY"
        );
      }
    } else {
      const isLiveKey = stripeConfig.publishableKey.startsWith("pk_live_");
      const isTestKey = stripeConfig.publishableKey.startsWith("pk_test_");

      if (!isLiveKey && !isTestKey) {
        errors.push(
          "Invalid Stripe key format - must start with pk_live_ or pk_test_"
        );
      }

      if (this.isProduction && !isLiveKey) {
        warnings.push("Using Stripe test keys in production environment");
      }

      if ((this.isLocal || this.isDevelopment || this.isStaging) && isLiveKey) {
        errors.push(
          "Using Stripe live keys in non-production environment - this is not allowed for security"
        );
      }

      info.push(`Stripe mode: ${isLiveKey ? "live" : "test"}`);
    }

    // Validate API configuration
    const apiConfig = this.api;
    if (!apiConfig.baseUrl) {
      warnings.push("API base URL not configured - API calls may fail");
    } else {
      // Validate URL format
      try {
        new URL(apiConfig.baseUrl);
        info.push(`API endpoint: ${apiConfig.baseUrl}`);
      } catch (e) {
        errors.push(`Invalid API base URL format: ${apiConfig.baseUrl}`);
      }
    }

    // Validate monitoring configuration for production
    if (this.isProduction) {
      const monitoring = this.monitoring;
      if (!monitoring.errorReporting) {
        warnings.push(
          "Error reporting disabled in production - consider enabling"
        );
      }
      if (!monitoring.analytics && !monitoring.googleAnalyticsId) {
        warnings.push("Analytics not configured in production");
      }
    }

    // Validate security settings
    const security = this.security;
    if (this.isProduction && !security.enableCSP) {
      warnings.push(
        "Content Security Policy disabled in production - consider enabling"
      );
    }

    // Environment-specific validations
    if (this.isLocal) {
      info.push("Running in local development mode with relaxed security");
    }

    if (this.isStaging) {
      info.push("Running in staging environment - use for testing only");
    }

    if (this.isProduction) {
      info.push("Running in production environment with enhanced security");

      // Production-specific requirements
      if (this.debugMode) {
        warnings.push("Debug mode enabled in production - consider disabling");
      }
    }

    return {
      errors,
      warnings,
      info,
      isValid: errors.length === 0,
      environment: this.environment,
    };
  }

  // Logging helper
  static log(level, message, ...args) {
    const logLevels = ["debug", "info", "warn", "error"];
    const currentLevelIndex = logLevels.indexOf(this.logLevel);
    const messageLevelIndex = logLevels.indexOf(level);

    if (messageLevelIndex >= currentLevelIndex) {
      console[level](`[${this.environment.toUpperCase()}]`, message, ...args);
    }
  }

  // Enhanced initialization with detailed reporting
  static initialize() {
    this.assertStripeKeyModeMatchesBackend();
    const validation = this.validateConfiguration();
    const buildInfo = this.buildInfo;

    // Log startup banner
    this.log("info", "🎵 ================================");
    this.log("info", "🎵 Crescendo Tutor App Starting");
    this.log("info", "🎵 ================================");
    this.log("info", `📍 Environment: ${this.environment.toUpperCase()}`);
    this.log("info", `🔧 Build Version: ${buildInfo.version}`);
    this.log("info", `🌿 Git Branch: ${buildInfo.gitBranch}`);
    this.log("info", `📝 Git Commit: ${buildInfo.gitCommit.substring(0, 8)}`);
    this.log("info", `⏰ Build Time: ${buildInfo.buildTime}`);

    // Environment-specific startup info
    if (this.isLocal) {
      this.log("info", "🏠 Local development mode enabled");
      this.log("info", `🐞 Debug Mode: ${this.debugMode ? "ON" : "OFF"}`);
    } else if (this.isStaging) {
      this.log("info", "🧪 Staging environment - for testing only");
    } else if (this.isProduction) {
      this.log("info", "🚀 Production environment - live system");
    }

    // Feature flags
    const features = this.features;
    this.log("debug", "🎛️  Feature Flags:");
    Object.entries(features).forEach(([key, value]) => {
      this.log("debug", `  ${key}: ${value ? "✅" : "❌"}`);
    });

    // Configuration info
    validation.info.forEach((infoMsg) => this.log("info", `ℹ️  ${infoMsg}`));

    // Handle validation errors
    if (validation.errors.length > 0) {
      this.log("error", "❌ Configuration Errors:");
      validation.errors.forEach((error) => this.log("error", `  💥 ${error}`));

      if (this.isProduction) {
        throw new Error(
          `Invalid configuration for production environment: ${validation.errors.join(
            "; "
          )}`
        );
      } else {
        this.log(
          "warn",
          "⚠️  Continuing with errors in non-production environment"
        );
      }
    }

    // Handle validation warnings
    if (validation.warnings.length > 0) {
      this.log("warn", "⚠️  Configuration Warnings:");
      validation.warnings.forEach((warning) =>
        this.log("warn", `  ⚡ ${warning}`)
      );
    }

    // Success message
    if (validation.isValid) {
      this.log("info", "✅ Configuration validated successfully");
    }

    // Performance info
    this.log(
      "debug",
      `🎯 Performance Monitoring: ${
        this.monitoring.performanceMonitoring ? "ON" : "OFF"
      }`
    );
    this.log(
      "debug",
      `📊 Analytics: ${this.monitoring.analytics ? "ON" : "OFF"}`
    );
    this.log("debug", `💾 Caching: ${this.cache.enabled ? "ON" : "OFF"}`);

    this.log("info", "🎵 ================================");
    this.log("info", "🎵 Startup Complete");
    this.log("info", "🎵 ================================");

    return validation;
  }

  // Utility methods for runtime environment checks
  static requiresAuth() {
    return this.security.requireAuth;
  }

  static shouldShowDebugInfo() {
    return this.debugMode && !this.isProduction;
  }

  static getEnvironmentBannerConfig() {
    return {
      show: !this.isProduction,
      environment: this.environment,
      color: this.isLocal ? "green" : this.isStaging ? "orange" : "red",
      message: this.isLocal
        ? "Development"
        : this.isStaging
        ? "Staging"
        : "Unknown",
    };
  }

  // Configuration export for debugging
  static exportConfiguration() {
    return {
      environment: this.environment,
      buildInfo: this.buildInfo,
      firebase: this.firebase,
      stripe: {
        environment: this.stripe.environment,
        hasKey: !!this.stripe.publishableKey,
      },
      api: this.api,
      features: this.features,
      payment: { ...this.payment, webhookEndpoint: "***" },
      monitoring: this.monitoring,
      security: this.security,
      cache: this.cache,
    };
  }
}

// Initialize on import if not in test environment
if (process.env.NODE_ENV !== "test") {
  EnvironmentConfig.initialize();
}

export default EnvironmentConfig;
